import { ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { AssessmentType, UnblockedBy } from "types/graphql-schema";
import { TFLocationState } from "types/topicflow";

import useCreateEmptyAssessment from "@apps/assessments/hooks/use-create-empty-assessment";
import useLabel from "@apps/use-label/use-label";
import AppPopover from "@components/popover/app-popover";

import {
  ComplianceProgramActionAssessmentItemProps,
  formatNameList,
} from "../helpers";
import ComplianceProgramActionItem from "./compliance-program-action-item";
import GoToResultsLink from "./go-to-results-link";

/**
 * "Waiting for pre-calibration". A click opens a popover that says who can end the wait,
 * and links a reader who can act to the Results tab.
 */
const BlockedReviewStatus = ({
  item,
}: {
  item: ComplianceProgramActionAssessmentItemProps;
}) => (
  <AppPopover
    placement="bottom-end"
    content={
      <div className="flex flex-col gap-3 px-4 pt-2 pb-3">
        <div>
          <div className="text-2xs text-gray-400 font-medium mb-0.5">
            Status
          </div>
          <div className="text-sm text-gray-700">
            {item.blockedText || item.statusText}
          </div>
        </div>
        {item.unblockedBy === UnblockedBy.Viewer && (
          <GoToResultsLink complianceProgramId={item.complianceProgram.id} />
        )}
      </div>
    }
  >
    <AppPopover.Button className="outline-none focus:outline-none focus:ring-0 focus-visible:ring-2 focus-visible:ring-blue-500 rounded -mx-1 px-1 text-sm text-gray-400 hover:text-gray-500">
      {item.statusText}
    </AppPopover.Button>
  </AppPopover>
);

const ComplianceProgramActionItemForAssessment = ({
  item,
  children,
}: {
  item: ComplianceProgramActionAssessmentItemProps;
  children?: ReactNode | null;
}) => {
  const label = useLabel();
  const location = useLocation<TFLocationState>();
  const { handleCreateEmptyAssessment, isLoading } = useCreateEmptyAssessment(
    location.pathname,
  );

  const isEngagementSurvey =
    item.assessmentType === AssessmentType.EngagementSurvey;
  const path = isEngagementSurvey ? `/survey` : `/assessments`;

  const handleClickCreateAssessment = () => {
    if (!isEngagementSurvey && !item.user) {
      throw new Error(
        "All assessment types (except engagement survey) must have a target user.",
      );
    }
    handleCreateEmptyAssessment(
      item.complianceProgram.id,
      item.assessmentTemplateId,
      !isEngagementSurvey && item.user ? item.user.id : undefined,
      path,
    );
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const {
    key: _key,
    otherManagerReviewers = [],
    ...extendedItem
  } = {
    ...item,
    buttonProps: {
      ...item.buttonProps,
      to: item.id ? `${path}/assessment/${item.id}` : undefined,
      onClick: item.id ? undefined : handleClickCreateAssessment,
      text: isLoading ? "Creating..." : item.buttonProps?.text,
      disabled: isLoading,
    },
  };
  const submittedBy = otherManagerReviewers.filter(
    (reviewer) => reviewer.submittedDatetime,
  );

  return (
    <>
      <ComplianceProgramActionItem
        {...extendedItem}
        // A blocked review cannot be started yet, so it gets no button. Its status says
        // who can end the wait instead, like a Blocked cell on the Progress tab.
        buttonProps={item.blocked ? undefined : extendedItem.buttonProps}
        statusText={item.blocked ? null : extendedItem.statusText}
        button={item.blocked ? <BlockedReviewStatus item={item} /> : null}
      >
        {children}
        {/* A blocked review cannot be submitted yet, so there is nothing to coordinate. */}
        {!item.blocked && submittedBy.length > 0 && (
          <span className="text-xs text-gray-500">
            {formatNameList(submittedBy.map((reviewer) => reviewer.user.name))}{" "}
            already submitted this {label("review")}.
          </span>
        )}
        {!item.blocked &&
          submittedBy.length === 0 &&
          otherManagerReviewers.length > 0 && (
            <span className="text-xs text-gray-500">
              {formatNameList(
                otherManagerReviewers.map((reviewer) => reviewer.user.name),
              )}{" "}
              can also write this {label("review")}. The first manager to submit
              completes it.
            </span>
          )}
      </ComplianceProgramActionItem>
    </>
  );
};

export default ComplianceProgramActionItemForAssessment;
